/**
 * On-demand video.js loader — the media-preview modal's player upgrade without
 * touching any page bundle.
 *
 * The library is a dynamic import, so webpack emits it as its own async chunk
 * that only downloads when a preview modal actually opens (same pattern as
 * pdf.js in the modal). The stylesheet is version-stamped in /public/vendor
 * (copied from node_modules at upgrade time — keep the filename in sync with
 * package.json) and injected as a <link> alongside the first load.
 *
 * Resolution contract: resolves with the videojs factory, or rejects — the
 * modal then falls back to the native <video>/<audio> elements it always had,
 * so a failed chunk (network blip, aggressive extension) degrades to exactly
 * the pre-video.js experience instead of a broken preview.
 */
const VIDEOJS_CSS_HREF = '/vendor/video-js-8.24.0.min.css';

let pending: Promise<any> | null = null;

function injectCssOnce(): Promise<void> {
    return new Promise(resolve => {
        const id = 'videojs-skin-css';
        if (document.getElementById(id)) return resolve();
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = VIDEOJS_CSS_HREF;
        // A missing skin is cosmetic, never fatal: resolve either way.
        link.onload = () => resolve();
        link.onerror = () => resolve();
        document.head.appendChild(link);
    });
}

export function loadVideoJs(): Promise<any> {
    if (!pending) {
        pending = Promise.all([
            import(/* webpackChunkName: "videojs" */ 'video.js').then(m => (m as any).default ?? m),
            injectCssOnce(),
        ]).then(([vjs]) => vjs);
        // A failed load must stay retryable on the next modal open.
        pending.catch(() => { pending = null; });
    }
    return pending;
}
